import './result-video.scss';

import { type ChangeEvent, type FC, useRef, useState } from 'react';

import { useResultVideo } from '../../../hooks/useResultVideo';
import { useUnitLock } from '../../../hooks/useUnitLock';
import { type IResultVideo, videoOffered } from '../../../lib/result-video-api';
import { canRecordInApp, deviceInfo, formatClock, formatDeadline, sha256Hex, SHOTS } from '../../../lib/result-video-shots';
import { isPending, uploadedPercent } from '../../../lib/result-video-store';
import { describeVideoRecord, isVideoSyncing, queueResultVideo } from '../../../lib/result-video-sync';
import ResultVideoRecorder, { type IPartyScore, type IRecordedVideo } from './ResultVideoRecorder';

interface IProps {
	electionId?: number;
	pollingUnitCode?: string;
	pollingUnitName?: string;
	resultReferenceId?: string;
	/** Figures the agent entered, shown as a read-along while filming the scores. */
	partyScores?: IPartyScore[];
	/** The agent ticked "the sheet itself shows these figures differing" before submitting. */
	flagged?: boolean;
	/** `success`: straight after Submit, with a Later option. `result`: the submitted-result view. */
	context: 'success' | 'result';
}

const readDuration = (file: File): Promise<number | undefined> =>
	new Promise((resolve) => {
		try {
			const url = URL.createObjectURL(file);
			const probe = document.createElement('video');
			probe.preload = 'metadata';
			const done = (value?: number) => {
				URL.revokeObjectURL(url);
				resolve(value);
			};
			probe.onloadedmetadata = () => done(Number.isFinite(probe.duration) ? probe.duration : undefined);
			probe.onerror = () => done(undefined);
			probe.src = url;
		} catch {
			resolve(undefined);
		}
	});

/**
 * The result video, wherever the agent meets it: the offer after Submit, the upload's progress,
 * the video once added, and the window closing. Renders nothing when the election does not offer
 * result videos or the server cannot store them.
 */
const ResultVideoCard: FC<IProps> = ({
	electionId,
	pollingUnitCode,
	pollingUnitName,
	resultReferenceId,
	partyScores,
	flagged,
	context,
}) => {
	const { policy, record, server, progress, send, refreshServer } = useResultVideo(electionId, pollingUnitCode);
	const lock = useUnitLock(electionId, pollingUnitCode, true);
	const [recording, setRecording] = useState(false);
	const [later, setLater] = useState(false);
	const [playing, setPlaying] = useState(false);
	// The player fired `error` (bad or expired link, server down). Mirrors admin's ResultVideoPanel.
	const [playbackFailed, setPlaybackFailed] = useState(false);
	const [fileError, setFileError] = useState('');
	// Two pickers: the gallery (no `capture`) and, where in-app recording is impossible, the OS camera.
	const fileRef = useRef<HTMLInputElement>(null);
	const cameraRef = useRef<HTMLInputElement>(null);

	if (!videoOffered(policy) || !electionId || !pollingUnitCode) return null;
	const { videoPolicy, maxDurationSec, maxBytes, windowHours } = policy.policy;

	const current: IResultVideo | null = server?.current ?? null;
	const pending = isPending(record) || record?.state === 'refused' || record?.state === 'missing';
	const windowOpen = server ? server.windowOpen : true;
	const deadline = formatDeadline(server?.windowClosesAt ?? record?.windowClosesAt);
	const reference = resultReferenceId || record?.resultReferenceId || current?.resultReferenceId || '';
	const strong = videoPolicy === 'Recommended' || videoPolicy === 'Required' || !!flagged;

	const queue = async (input: Pick<IRecordedVideo, 'file'> & Partial<IRecordedVideo>, mode: 'Live' | 'Uploaded') => {
		await queueResultVideo(
			{
				electionId,
				pollingUnitCode,
				pollingUnitName,
				resultReferenceId: reference,
				file: input.file,
				fileName: input.file.name,
				captureMode: mode,
				durationSec: input.durationSec,
				captureStartedAt: input.captureStartedAt,
				captureEndedAt: input.captureEndedAt,
				coords: input.coords ?? null,
				chapters: input.chapters ?? [],
				deviceInfo: input.deviceInfo ?? deviceInfo(),
				windowClosesAt: server?.windowClosesAt ?? undefined,
				wifiOnly: input.wifiOnly,
			},
			{ sha256: sha256Hex }
		);
	};

	const onRecorded = (video: IRecordedVideo) => {
		setRecording(false);
		void queue(video, 'Live');
	};

	const onFilePicked = async (file: File | undefined) => {
		setFileError('');
		if (!file) return;
		if (!file.type.startsWith('video/')) {
			setFileError('Choose a video file.');
			return;
		}
		if (file.size > maxBytes) {
			setFileError(`This video is larger than ${Math.round(maxBytes / (1024 * 1024))} MB. Record a shorter one.`);
			return;
		}
		const durationSec = await readDuration(file);
		if (durationSec && durationSec > maxDurationSec + 5) {
			setFileError(`This video is longer than ${formatClock(maxDurationSec)}. Record a shorter one.`);
			return;
		}
		void queue({ file, durationSec }, 'Uploaded');
	};

	const startRecording = () => {
		if (canRecordInApp()) setRecording(true);
		else cameraRef.current?.click();
	};

	const onPickerChange = (e: ChangeEvent<HTMLInputElement>) => {
		void onFilePicked(e.target.files?.[0]);
		e.target.value = '';
	};

	const picker = (
		<>
			<input
				ref={fileRef}
				type='file'
				accept='video/*'
				className='d-none'
				aria-hidden='true'
				tabIndex={-1}
				onChange={onPickerChange}
			/>
			<input
				ref={cameraRef}
				type='file'
				accept='video/*'
				capture='environment'
				className='d-none'
				aria-hidden='true'
				tabIndex={-1}
				onChange={onPickerChange}
			/>
		</>
	);

	const recorder = recording ? (
		<ResultVideoRecorder
			pollingUnitCode={pollingUnitCode}
			pollingUnitName={pollingUnitName}
			partyScores={partyScores}
			maxDurationSec={maxDurationSec}
			onUse={onRecorded}
			onClose={() => setRecording(false)}
		/>
	) : null;

	// ── 1. this phone is still sending a video (or could not) ──
	if (record && pending) {
		const syncing = isVideoSyncing(electionId, pollingUnitCode);
		const status = describeVideoRecord(record, progress, syncing);
		const percent = progress ?? uploadedPercent(record);
		const canRetry =
			record.state === 'held' ||
			(!syncing && (record.state === 'uploading' || record.state === 'queued' || record.state === 'uploaded'));
		const canRedo =
			(record.state === 'missing' || record.state === 'refused') && windowOpen && record.code !== 'RESULT_VIDEO_WINDOW_CLOSED';
		return (
			<section
				className={`rv-card rv-card--${status.tone}`}
				aria-label='Result video'
			>
				<div className='rv-card__row'>
					<span className='rv-card__title'>Result video</span>
					<span className='rv-card__meta'>{record.durationSec ? formatClock(record.durationSec) : ''}</span>
				</div>
				<p
					className='rv-card__status'
					aria-live='polite'
				>
					{status.text}
				</p>
				{record.state === 'uploading' || record.state === 'queued' || record.state === 'uploaded' ? (
					<div
						className='rv-card__bar'
						role='progressbar'
						aria-valuemin={0}
						aria-valuemax={100}
						aria-valuenow={percent}
						aria-label='Upload progress'
					>
						<span style={{ width: `${Math.max(2, percent)}%` }} />
					</div>
				) : null}
				<div className='rv-card__actions'>
					{canRetry ? (
						<button
							type='button'
							className='rv-card__btn rv-card__btn--ghost'
							onClick={() => send(true)}
						>
							{record.state === 'queued' && record.wifiOnly ? 'Upload now' : 'Retry now'}
						</button>
					) : null}
					{canRedo ? (
						<button
							type='button'
							className='rv-card__btn'
							onClick={startRecording}
						>
							Record again
						</button>
					) : null}
				</div>
				{picker}
				{recorder}
			</section>
		);
	}

	// ── 2. a video is on the result ──
	if (current) {
		const verified = current.captureMode === 'Live';
		// A video stored on the retired provider is on the record but can't be played back.
		const playable = current.playable !== false && !!current.viewUrl;
		return (
			<section
				className='rv-card rv-card--ok'
				aria-label='Result video'
			>
				<div className='rv-card__row'>
					<span className='rv-card__title'>Result video</span>
					<span className={`rv-card__badge${verified ? ' rv-card__badge--verified' : ''}`}>
						{verified ? 'Verified capture' : 'Uploaded file'}
					</span>
				</div>
				{!playable ? (
					<p className='rv-card__note'>Video added to your result. It can’t be played back on this phone.</p>
				) : playbackFailed ? (
					<div
						className='rv-card__playback-error'
						role='alert'
					>
						<p className='rv-card__note'>
							{Date.parse(current.viewUrlExpiresAt) <= Date.now()
								? 'The video link has expired.'
								: 'This video didn’t play. Reload to try again.'}
						</p>
						<button
							type='button'
							className='rv-card__btn rv-card__btn--ghost'
							onClick={() => {
								// A fresh state carries a fresh viewUrl; the player remounts on its expiry key.
								setPlaybackFailed(false);
								setPlaying(true);
								void refreshServer();
							}}
						>
							Reload video
						</button>
					</div>
				) : playing ? (
					// eslint-disable-next-line jsx-a11y/media-has-caption -- field recording, no caption track exists
					<video
						key={`${current.id}-${current.viewUrlExpiresAt}`}
						onError={() => setPlaybackFailed(true)}
						className='rv-card__player'
						src={current.viewUrl}
						poster={current.thumbnailUrl ?? undefined}
						controls
						autoPlay
						playsInline
					/>
				) : (
					<button
						type='button'
						className='rv-card__poster'
						onClick={() => setPlaying(true)}
						aria-label='Play the result video'
					>
						{current.thumbnailUrl ? (
							<img
								src={current.thumbnailUrl}
								alt=''
								loading='lazy'
							/>
						) : null}
						<span className='rv-card__play'>Play {current.durationSec ? formatClock(current.durationSec) : ''}</span>
					</button>
				)}
				{current.chapters.length > 1 ? (
					<p className='rv-card__note'>
						{current.chapters.length >= SHOTS.length
							? `All ${SHOTS.length} shots filmed`
							: `${current.chapters.length} of ${SHOTS.length} shots filmed`}
					</p>
				) : null}
				<div className='rv-card__actions'>
					{windowOpen ? (
						<>
							<span className='rv-card__note'>{deadline ? `You can replace it until ${deadline}.` : null}</span>
							<button
								type='button'
								className='rv-card__btn rv-card__btn--ghost'
								onClick={startRecording}
							>
								Record again
							</button>
						</>
					) : (
						<span className='rv-card__note'>
							{server?.windowClosedReason === 'reviewed'
								? 'Your result has been reviewed, so the video is final.'
								: 'The video is final.'}
						</span>
					)}
				</div>
				{picker}
				{recorder}
			</section>
		);
	}

	// ── 3a. nothing yet, and INEC cancelled the unit — no new video until it is reinstated ──
	if (lock.locked) {
		return context === 'result' ? (
			<section
				className='rv-card rv-card--muted'
				aria-label='Result video'
			>
				<span className='rv-card__title'>Result video</span>
				<p className='rv-card__status'>INEC cancelled this unit. A video can be added once your party reinstates it.</p>
			</section>
		) : null;
	}

	// ── 3. nothing yet, and the window has closed ──
	if (!windowOpen) {
		return context === 'result' ? (
			<section
				className='rv-card rv-card--muted'
				aria-label='Result video'
			>
				<span className='rv-card__title'>Result video</span>
				<p className='rv-card__status'>
					{server?.windowClosedReason === 'reviewed'
						? 'Your result has been reviewed, so a video can no longer be added.'
						: 'The time to add a video for this result has passed.'}
				</p>
			</section>
		) : null;
	}

	// ── 4. the offer ──
	if (later && context === 'success') {
		return (
			<p className='rv-card__later'>
				You can add a video later from <strong>View Result</strong>
				{deadline ? ` until ${deadline}` : ` within ${windowHours} hours`}.
			</p>
		);
	}

	return (
		<section
			className={`rv-card rv-card--offer${strong ? ' rv-card--strong' : ''}`}
			aria-labelledby='rv-offer-title'
		>
			<span
				id='rv-offer-title'
				className='rv-card__title'
			>
				{videoPolicy === 'Required' ? 'Add a video of the result sheet' : 'Add a video of the result sheet?'}
			</span>
			<p className='rv-card__lead'>
				{flagged
					? 'Your result has figures that don’t balance on the sheet. A video of the sheet helps it get verified.'
					: 'A short video shows reviewers the sheet was signed at your polling unit. Your result is already submitted either way.'}
			</p>
			<ol
				className='rv-card__shots'
				aria-label='What you will film'
			>
				<li>The polling unit</li>
				<li>The whole sheet</li>
				<li>The party scores</li>
				<li>Signatures and stamp</li>
			</ol>
			<p className='rv-card__note'>
				Up to {formatClock(maxDurationSec)}. It uploads in the background, even with weak signal
				{deadline ? `. Add it by ${deadline}` : `. Add it within ${windowHours} hours`}.
			</p>
			{fileError ? (
				<p
					className='rv-card__error'
					role='alert'
				>
					{fileError}
				</p>
			) : null}
			<div className='rv-card__actions'>
				<button
					type='button'
					className='rv-card__btn rv-card__btn--primary'
					onClick={startRecording}
				>
					Record video
				</button>
				{context === 'success' ? (
					<button
						type='button'
						className='rv-card__btn rv-card__btn--ghost'
						onClick={() => setLater(true)}
					>
						Later
					</button>
				) : null}
			</div>
			<button
				type='button'
				className='rv-card__link'
				onClick={() => fileRef.current?.click()}
			>
				Choose a video from your phone instead
			</button>
			{picker}
			{recorder}
		</section>
	);
};

export default ResultVideoCard;
